import { useState, useEffect, useRef } from "react";
import { Truck, PackageCheck, Save, FileDown, Link2, Loader2, Copy, Check, ShoppingCart, Plus, Trash2, Search, PackageSearch, MapPin, Building2, MessageSquareText, Ban, Settings2, Upload, X, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { computeQuote } from "./QuoteBuilder";
import { api, formatApiError } from "../../lib/api";

const input = "bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet transition-colors text-sm";
const money = (n, c = "MXN") => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${c}`;
const UNIDADES = ["pieza", "servicio", "set", "metro lineal", "m²", "kg", "litro", "caja", "paquete", "hora", "lote", "kit"];

// Órdenes de compra de Retail: partidas con SKU · Concepto · Cantidad · Precio,
// jalando productos del catálogo (retail_products) o agregándolos a mano.
export default function RetailPOItems({ item, onRefresh }) {
  const [quote, setQuote] = useState(item.quote || { items: [], tax_pct: 16, tax_included: false });
  const [delivery, setDelivery] = useState(item.delivery_method || "recepcion_cedis");
  const [deliveryNote, setDeliveryNote] = useState(item.delivery_note || item.cedis || "");
  const [deliveryAddress, setDeliveryAddress] = useState(item.delivery_address || "");
  const [prov, setProv] = useState({
    rfc: item.proveedor_rfc || "", telefono: item.proveedor_telefono || "", email: item.proveedor_email || "",
    contacto: item.proveedor_contacto || "", direccion: item.proveedor_direccion || "",
  });
  const [directorio, setDirectorio] = useState([]);
  const [comentarios, setComentarios] = useState(item.comentarios || "");

  useEffect(() => {
    api.get("/retail/proveedores").then((r) => {
      const dir = r.data || [];
      setDirectorio(dir);
      const vacio = !(item.proveedor_rfc || item.proveedor_telefono || item.proveedor_email || item.proveedor_contacto || item.proveedor_direccion);
      if (vacio && item.proveedor) {
        const match = dir.find((d) => (d.nombre || "").trim().toLowerCase() === item.proveedor.trim().toLowerCase());
        if (match && (match.rfc || match.telefono || match.email || match.contacto || match.direccion)) {
          setProv({ rfc: match.rfc || "", telefono: match.telefono || "", email: match.email || "", contacto: match.contacto || "", direccion: match.direccion || "" });
          toast.info(`Datos de "${match.nombre}" autocompletados desde el directorio de proveedores`);
        }
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fillFromDirectorio = (id) => {
    const d = directorio.find((x) => x.id === id);
    if (!d) return;
    setProv({ rfc: d.rfc || "", telefono: d.telefono || "", email: d.email || "", contacto: d.contacto || "", direccion: d.direccion || "" });
    toast.success(`Datos de "${d.nombre}" aplicados`);
  };
  const [currency, setCurrency] = useState(item.quote?.currency || item.moneda || "MXN");
  const [busy, setBusy] = useState("");
  const [token, setToken] = useState(item.public_token || null);
  const [copied, setCopied] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerRef = useRef();

  // Logo de la empresa (sale en el PDF y la liga; compartido con Studio Cotizaciones)
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef();
  const loadLogo = async () => {
    try {
      const res = await api.get(`/studio/quote-logo`, { responseType: "blob" });
      setLogoUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch { setLogoUrl(null); }
  };
  useEffect(() => { loadLogo(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const uploadLogo = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setLogoBusy(true);
    try { await api.post(`/studio/quote-logo`, fd); toast.success("Logo actualizado · saldrá en el PDF y la liga"); await loadLogo(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir el logo"); } finally { setLogoBusy(false); }
  };
  const deleteLogo = async () => {
    try { await api.delete(`/studio/quote-logo`); setLogoUrl((p) => { if (p) URL.revokeObjectURL(p); return null; }); toast.success("Logo eliminado"); }
    catch { toast.error("No se pudo eliminar el logo"); }
  };

  // Folio automático configurable (prefijo + número inicial, ej. OC-1206)
  const [folioOpen, setFolioOpen] = useState(false);
  const [folioCfg, setFolioCfg] = useState({ prefix: "OC-", next_number: "" });
  useEffect(() => {
    api.get("/retail/po-folio-config")
      .then((r) => setFolioCfg({ prefix: r.data.prefix ?? "OC-", next_number: r.data.next_number ?? "" }))
      .catch(() => {});
  }, []);
  const saveFolioCfg = async () => {
    const n = parseInt(folioCfg.next_number, 10);
    if (!n || n < 1) { toast.error("Indica el número inicial (ej. 1206)"); return; }
    try {
      await api.put("/retail/po-folio-config", { prefix: folioCfg.prefix || "", next_number: n });
      toast.success(`Folio automático guardado: la próxima orden nueva será ${(folioCfg.prefix || "")}${n}`);
      setFolioOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); }
  };

  useEffect(() => { api.get("/retail/products").then((r) => setCatalog(r.data || [])).catch(() => setCatalog([])); }, []);
  useEffect(() => {
    const h = (e) => { if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, []);

  const items = quote.items || [];
  const setItems = (next) => setQuote((q) => ({ ...q, items: next }));
  const setLine = (i, patch) => setItems(items.map((it, j) => (j === i ? { ...it, ...patch } : it)));
  const rmLine = (i) => setItems(items.filter((_, j) => j !== i));
  const addManual = () => setItems([...items, { id: String(Date.now()), kind: "item", sku: "", description: "", qty: 1, unit: "pieza", unit_price: 0, discount_pct: 0 }]);
  const addFromProduct = (p) => {
    setItems([...items, { id: String(Date.now()), kind: "item", sku: p.sku || "", description: p.descripcion || p.sku || "", qty: 1, unit: "pieza", unit_price: Number(p.costo || p.precio || 0), discount_pct: 0, product_id: p.id }]);
    setPickerOpen(false); setSearch("");
  };

  const c = computeQuote(quote);
  const filtered = catalog.filter((p) => {
    const s = search.trim().toLowerCase();
    if (!s) return true;
    return `${p.sku || ""} ${p.descripcion || ""} ${p.marca || ""}`.toLowerCase().includes(s);
  }).slice(0, 40);

  const payload = () => ({
    quote: { ...quote, currency }, delivery_method: delivery, delivery_note: deliveryNote,
    delivery_address: deliveryAddress, comentarios,
    proveedor_rfc: prov.rfc, proveedor_telefono: prov.telefono, proveedor_email: prov.email,
    proveedor_contacto: prov.contacto, proveedor_direccion: prov.direccion,
  });

  const save = async () => {
    setBusy("save");
    try { await api.put(`/retail/purchase-orders/${item.id}/quote`, payload()); toast.success("Orden de compra guardada"); onRefresh && onRefresh(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo guardar"); } finally { setBusy(""); }
  };
  const ensureSaved = async () => { await api.put(`/retail/purchase-orders/${item.id}/quote`, payload()); };
  const downloadPdf = async () => {
    if (!items.length) { toast.error("Agrega al menos un producto"); return; }
    setBusy("pdf");
    try { await ensureSaved(); const res = await api.get(`/retail/purchase-orders/${item.id}/pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(res.data), "_blank"); }
    catch { toast.error("No se pudo generar el PDF"); } finally { setBusy(""); }
  };
  const share = async () => {
    if (!items.length) { toast.error("Agrega al menos un producto"); return; }
    setBusy("share");
    try { await ensureSaved(); const { data } = await api.post(`/retail/purchase-orders/${item.id}/share`); setToken(data.token); toast.success("Enlace público generado"); }
    catch { toast.error("No se pudo generar el enlace"); } finally { setBusy(""); }
  };

  const publicUrl = token ? `${window.location.origin}/oc/${token}` : "";
  const DeliveryCard = ({ id, icon: Icon, title, sub }) => (
    <button type="button" onClick={() => { setDelivery(id); if (id === "no_aplica") { setDeliveryNote(""); setDeliveryAddress(""); } }} data-testid={`po-delivery-${id}`}
      className={`text-left rounded-xl border p-3 flex items-start gap-3 transition-colors ${delivery === id ? "border-violet bg-violet/[0.04]" : "border-navy/10 hover:border-navy/25"}`}>
      <Icon className={`w-5 h-5 mt-0.5 ${delivery === id ? "text-violet" : "text-navy/40"}`} />
      <span><span className="block text-sm font-medium text-navy">{title}</span><span className="block text-xs text-navy/50">{sub}</span></span>
    </button>
  );

  return (
    <div className="mt-4 border-t border-navy/10 pt-4 space-y-4" data-testid="po-pricing">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 overline text-navy/40"><ShoppingCart className="w-3.5 h-3.5" /> Productos, IVA y entrega</div>
        <div className="flex flex-wrap gap-2 items-center">
          <select value={currency} onChange={(e) => setCurrency(e.target.value)} className={`${input} py-1.5`} data-testid="po-currency">
            <option value="MXN">MXN</option><option value="USD">USD</option><option value="EUR">EUR</option>
          </select>
          <button onClick={() => setFolioOpen((o) => !o)} className="btn btn-ghost text-sm" data-testid="po-folio-btn"><Settings2 className="w-4 h-4" /> Folio</button>
          <button onClick={downloadPdf} disabled={busy} className="btn btn-ghost text-sm" data-testid="po-price-pdf">{busy === "pdf" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} PDF</button>
          <button onClick={share} disabled={busy} className="btn btn-ghost text-sm" data-testid="po-price-share">{busy === "share" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />} Enlace</button>
          <button onClick={save} disabled={busy} className="btn btn-primary text-sm" data-testid="po-price-save">{busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Guardar</button>
        </div>
      </div>

      {token && (
        <div className="rounded-xl border border-violet/30 bg-violet/[0.03] p-3 flex items-center gap-2 text-sm" data-testid="po-public-url">
          <Link2 className="w-4 h-4 text-violet shrink-0" /><span className="text-navy/70 truncate flex-1">{publicUrl}</span>
          <button onClick={() => { navigator.clipboard?.writeText(publicUrl); setCopied(true); }} className="text-violet" data-testid="po-copy-url">{copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}</button>
        </div>
      )}

      {folioOpen && (
        <div className="rounded-xl border border-navy/10 bg-navy/[0.02] p-3 flex flex-wrap items-end gap-3" data-testid="po-folio-config">
          <div><label className="text-xs text-navy/50 block">Prefijo</label>
            <input value={folioCfg.prefix} onChange={(e) => setFolioCfg({ ...folioCfg, prefix: e.target.value })} className={`${input} w-24`} data-testid="po-folio-prefix" /></div>
          <div><label className="text-xs text-navy/50 block">Próximo número (ej. 1206)</label>
            <input type="number" min={1} value={folioCfg.next_number} onChange={(e) => setFolioCfg({ ...folioCfg, next_number: e.target.value })} className={`${input} w-36`} data-testid="po-folio-next" /></div>
          <button onClick={saveFolioCfg} className="btn btn-primary text-sm" data-testid="po-folio-save"><Save className="w-4 h-4" /> Guardar folio</button>
          <p className="text-[11px] text-navy/40 w-full">Las nuevas órdenes que se creen sin folio recibirán el consecutivo automáticamente (ej. {(folioCfg.prefix || "") + (folioCfg.next_number || "…")}).</p>
        </div>
      )}

      {/* Logo de la empresa */}
      <div className="rounded-xl border border-navy/10 p-3 flex items-center gap-3 flex-wrap" data-testid="po-logo-block">
        {logoUrl
          ? <img src={logoUrl} alt="Logo" className="h-10 max-w-[160px] object-contain" data-testid="po-logo-img" />
          : <span className="text-xs text-navy/40 flex items-center gap-1.5"><ImageIcon className="w-4 h-4" /> Sin logo — súbelo para que salga en el PDF y la liga</span>}
        <div className="ml-auto flex gap-2">
          <input ref={logoInputRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => { uploadLogo(e.target.files?.[0]); e.target.value = ""; }} data-testid="po-logo-input" />
          <button onClick={() => logoInputRef.current?.click()} disabled={logoBusy} className="btn btn-ghost text-xs" data-testid="po-logo-upload">{logoBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} {logoUrl ? "Cambiar logo" : "Subir logo"}</button>
          {logoUrl && <button onClick={deleteLogo} className="btn btn-ghost text-xs text-destructive" data-testid="po-logo-delete"><X className="w-3.5 h-3.5" /> Quitar</button>}
        </div>
      </div>

      {/* Datos del proveedor */}
      <div className="rounded-xl border border-navy/10 p-3 space-y-2" data-testid="po-supplier-data">
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-2 overline text-navy/40"><Building2 className="w-3.5 h-3.5" /> Datos del proveedor (salen en el PDF y la liga)</div>
          {directorio.length > 0 && (
            <select className={`${input} ml-auto max-w-[260px]`} value="" data-testid="po-prov-directorio"
              onChange={(e) => { if (e.target.value) fillFromDirectorio(e.target.value); e.target.value = ""; }}>
              <option value="">Autocompletar desde directorio…</option>
              {directorio.map((d) => <option key={d.id} value={d.id}>{d.nombre}</option>)}
            </select>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-2">
          <input value={prov.rfc} onChange={(e) => setProv({ ...prov, rfc: e.target.value })} placeholder="RFC" className={input} data-testid="po-prov-rfc" />
          <input value={prov.contacto} onChange={(e) => setProv({ ...prov, contacto: e.target.value })} placeholder="Nombre del contacto" className={input} data-testid="po-prov-contacto" />
          <input value={prov.telefono} onChange={(e) => setProv({ ...prov, telefono: e.target.value })} placeholder="Teléfono" className={input} data-testid="po-prov-telefono" />
          <input type="email" value={prov.email} onChange={(e) => setProv({ ...prov, email: e.target.value })} placeholder="E-mail" className={input} data-testid="po-prov-email" />
          <input value={prov.direccion} onChange={(e) => setProv({ ...prov, direccion: e.target.value })} placeholder="Dirección completa (calle, número, colonia, CP, ciudad, estado)" className={`${input} sm:col-span-2`} data-testid="po-prov-direccion" />
        </div>
        <p className="text-[11px] text-navy/35">Al guardar la orden, estos datos se guardan/actualizan solos en tu directorio (Proveedores Retail) y se autocompletan en las próximas órdenes de "{item.proveedor || "este proveedor"}".</p>
      </div>

      {/* Selector de producto del catálogo */}
      <div className="relative" ref={pickerRef}>
        <div className="flex items-center gap-2 rounded-xl border border-navy/15 px-3" data-testid="po-product-picker">
          <Search className="w-4 h-4 text-navy/40 shrink-0" />
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPickerOpen(true); }} onFocus={() => setPickerOpen(true)}
            placeholder="Buscar producto del catálogo por SKU o descripción…" className="w-full bg-transparent py-2.5 text-sm outline-none" data-testid="po-product-search" />
        </div>
        {pickerOpen && (
          <div className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-xl border border-navy/10 bg-white shadow-lg" data-testid="po-product-list">
            {filtered.length === 0
              ? <div className="p-4 text-sm text-navy/40 flex items-center gap-2"><PackageSearch className="w-4 h-4" /> {catalog.length === 0 ? "No hay productos en el catálogo (Retail Command Center → Productos & SKUs)." : "Sin coincidencias."}</div>
              : filtered.map((p) => (
                <button key={p.id} onClick={() => addFromProduct(p)} data-testid={`po-product-opt-${p.id}`}
                  className="w-full text-left px-4 py-2.5 hover:bg-violet/[0.05] border-b border-navy/5 last:border-0 flex items-center justify-between gap-3">
                  <span className="min-w-0"><span className="block text-sm text-navy truncate">{p.descripcion}</span><span className="block text-xs text-navy/40">{p.sku ? `SKU ${p.sku}` : "Sin SKU"}{p.marca ? ` · ${p.marca}` : ""}</span></span>
                  <span className="text-xs text-navy/60 shrink-0">{money(p.costo || p.precio, currency)}</span>
                </button>
              ))}
          </div>
        )}
      </div>

      {/* Tabla de partidas */}
      <div className="overflow-x-auto rounded-xl border border-navy/10">
        <table className="w-full text-sm" data-testid="po-items-table">
          <thead><tr className="bg-navy/[0.03] text-navy/50 text-xs">
            <th className="text-left font-medium p-2.5 w-28">SKU</th>
            <th className="text-left font-medium p-2.5">Concepto</th>
            <th className="text-right font-medium p-2.5 w-20">Cantidad</th>
            <th className="text-left font-medium p-2.5 w-32">Unidad</th>
            <th className="text-right font-medium p-2.5 w-28">Precio</th>
            <th className="text-right font-medium p-2.5 w-28">Importe</th>
            <th className="w-8" />
          </tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-5 text-center text-navy/40 text-sm">Aún no hay productos. Búscalos arriba o agrégalos manualmente.</td></tr>}
            {items.map((it, i) => {
              const line = Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount_pct || 0) / 100);
              return (
                <tr key={it.id || i} className="border-t border-navy/5" data-testid={`po-item-row-${i}`}>
                  <td className="p-1.5"><input value={it.sku || ""} onChange={(e) => setLine(i, { sku: e.target.value })} placeholder="SKU" className={`${input} w-full`} data-testid={`po-item-sku-${i}`} /></td>
                  <td className="p-1.5"><input value={it.description || ""} onChange={(e) => setLine(i, { description: e.target.value })} placeholder="Concepto" className={`${input} w-full`} data-testid={`po-item-desc-${i}`} /></td>
                  <td className="p-1.5"><input type="number" value={it.qty} onChange={(e) => setLine(i, { qty: e.target.value })} className={`${input} w-full text-right`} data-testid={`po-item-qty-${i}`} /></td>
                  <td className="p-1.5">
                    <select value={it.unit || "pieza"} onChange={(e) => setLine(i, { unit: e.target.value })} className={`${input} w-full`} data-testid={`po-item-unit-${i}`}>
                      {UNIDADES.map((u) => <option key={u} value={u}>{u}</option>)}
                      {it.unit && !UNIDADES.includes(it.unit) && <option value={it.unit}>{it.unit}</option>}
                    </select>
                  </td>
                  <td className="p-1.5"><input type="number" value={it.unit_price} onChange={(e) => setLine(i, { unit_price: e.target.value })} className={`${input} w-full text-right`} data-testid={`po-item-price-${i}`} /></td>
                  <td className="p-2.5 text-right text-navy/70 whitespace-nowrap">{money(line, currency)}</td>
                  <td className="p-1.5 text-center"><button onClick={() => rmLine(i)} className="text-navy/30 hover:text-destructive" data-testid={`po-item-del-${i}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button onClick={addManual} className="text-sm text-violet hover:underline flex items-center gap-1" data-testid="po-add-manual"><Plus className="w-4 h-4" /> Agregar producto manual</button>

      {/* IVA + saldo a favor + totales */}
      <div className="flex flex-wrap items-end justify-between gap-4 pt-3 border-t border-navy/10">
        <div className="flex flex-wrap items-end gap-4">
          <label className="flex items-center gap-2 text-xs text-navy/70 cursor-pointer pb-2.5" data-testid="po-tax-included">
            <input type="checkbox" checked={!!quote.tax_included} onChange={(e) => setQuote((q) => ({ ...q, tax_included: e.target.checked }))} />
            El IVA ya está incluido en los precios
          </label>
          <div><label className="text-xs text-navy/50 block">IVA %</label><input type="number" value={quote.tax_pct ?? 16} onChange={(e) => setQuote((q) => ({ ...q, tax_pct: e.target.value }))} className={`${input} w-20`} data-testid="po-tax-pct" /></div>
          <div>
            <label className="text-xs text-navy/50 block">Saldo a favor (descuento antes del IVA)</label>
            <input type="number" min={0} value={quote.credit_amount ?? ""} placeholder="0.00"
              onChange={(e) => setQuote((q) => ({ ...q, credit_amount: e.target.value }))}
              className={`${input} w-44`} data-testid="po-credit-amount" />
          </div>
        </div>
        <div className="text-right space-y-0.5">
          <div className="text-xs text-navy/50" data-testid="po-breakdown">
            Subtotal {money(c.subtotal + c.discount, currency)}
            {c.discount ? ` · Desc. −${money(c.discount, currency)}` : ""}
            {c.credit ? ` · Saldo a favor −${money(c.credit, currency)}` : ""}
            {c.tax ? ` · IVA${quote.tax_included ? " incl." : ""} ${money(c.tax, currency)}` : ""}
          </div>
          <div className="text-2xl font-medium text-navy" data-testid="po-total">{money(c.total, currency)}</div>
        </div>
      </div>

      {/* Método de entrega */}
      <div>
        <label className="text-xs text-navy/50 block mb-2">Método de entrega</label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <DeliveryCard id="recepcion_cedis" icon={PackageCheck} title="Recepción en CEDIS" sub="El proveedor entrega en nuestro CEDIS" />
          <DeliveryCard id="recoleccion_proveedor" icon={Truck} title="Recolección en CEDIS del proveedor" sub="Nosotros recolectamos en su CEDIS" />
          <DeliveryCard id="entrega_direccion" icon={MapPin} title="Entrega en dirección específica" sub="El proveedor entrega en la dirección que indiques" />
          <DeliveryCard id="no_aplica" icon={Ban} title="No aplica" sub="Sin entrega física (ej. servicios)" />
        </div>
        {delivery === "entrega_direccion" && (
          <input value={deliveryAddress} onChange={(e) => setDeliveryAddress(e.target.value)}
            placeholder="Dirección de entrega completa (calle, número, colonia, CP, ciudad, estado)"
            className={`${input} w-full mt-2 border-violet/40`} data-testid="po-delivery-address" />
        )}
        {delivery !== "no_aplica" && (
          <input value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder="CEDIS / nota de entrega (opcional)" className={`${input} w-full mt-2`} data-testid="po-delivery-note" />
        )}
      </div>

      {/* Comentarios */}
      <div data-testid="po-comments-block">
        <label className="text-xs text-navy/50 mb-1 flex items-center gap-1.5"><MessageSquareText className="w-3.5 h-3.5" /> Comentarios (salen en el PDF y la liga)</label>
        <textarea value={comentarios} onChange={(e) => setComentarios(e.target.value)} rows={3}
          placeholder="Ej.: Favor de confirmar existencias antes de embarcar. Entregar con cita…"
          className={`${input} w-full`} data-testid="po-comentarios" />
      </div>
    </div>
  );
}
